import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import "./Layout.css"; // Import the CSS file
import LegalFooter from "./LegalFooter";

// The "App" link points at /analysis, but it represents the whole product —
// the Analysis, Screener, and Market Health tabs that live inside the
// FunVizTabs shell. So it should read as active on any of those tab routes,
// not just /analysis. Listed here (rather than derived) so adding a new app
// tab is an explicit, one-line change.
const APP_TAB_PATHS = ["/analysis", "/screener", "/markethealth"];

// A small, subtle utility nav in the top bar (finviz-inspired, but deliberately
// sparse — FunViz is a single-purpose tool, not a market portal). Three text
// links only: Home (the landing page), App (the main product on the Analysis
// screen — named "App" rather than "FunViz"/"Analysis" to avoid brand-name
// redundancy and to not duplicate the FunVizTabs "Analysis" tab label), and
// Blog (the research page). Section navigation between Analysis/Screener/
// Market Health still lives in the FunVizTabs tab bar below.
const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/analysis", label: "App", end: false },
  { to: "/blog", label: "Blog", end: false },
];

// The app-wide top bar. FunViz is the whole product now, so the bar is pure
// branding + account chrome rather than a multi-page nav: the FunViz logo and
// wordmark on the left (a link back to the Analysis home), and a mock account
// menu on the right — a user-icon button that opens a small popover. The
// popover is intentionally inert for now: it reports "Not signed in" and
// offers no sign-in action yet; auth is a later milestone. Section navigation
// (Analysis / Screener) lives in the FunVizTabs tab bar below the top bar.
const Layout = () => {
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const location = useLocation();

  // Is the current route one of the app tabs? Used to keep "App" highlighted
  // across Analysis / Screener / Market Health, not only on /analysis.
  const isAppTabActive = APP_TAB_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  // Dismiss the account popover on outside click or Escape — the standard
  // dismiss affordances for a menu opened from a toolbar button.
  useEffect(() => {
    if (!isAccountOpen) return undefined;

    const handlePointerDown = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setIsAccountOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isAccountOpen]);

  return (
    <>
      <nav className="top-bar">
        <div className="nav-links" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => {
                // "App" stays active across all app-tab routes (Analysis /
                // Screener / Market Health), not just its own /analysis target.
                const active =
                  link.to === "/analysis" ? isAppTabActive : isActive;
                return active ? "nav-link nav-link--active" : "nav-link";
              }}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <Link to="/" className="brand" aria-label="FunViz home">
          <img src="/logo192.png" alt="" className="brand-logo" />
          <span className="brand-name">FunViz</span>
          <span className="brand-tagline">Fundamentals visualizer</span>
        </Link>

        <div className="account" ref={accountRef}>
          <button
            type="button"
            className="account-button"
            aria-label="Account"
            aria-haspopup="dialog"
            aria-expanded={isAccountOpen}
            onClick={() => setIsAccountOpen((open) => !open)}
          >
            {/* Inline user icon (matches the app's existing inline-SVG icon
                approach rather than pulling in an icon-font dependency). */}
            <svg
              className="account-icon"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.24-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.76-3.58-5-8-5Z"
              />
            </svg>
          </button>

          {isAccountOpen && (
            <div className="account-popover" role="dialog" aria-label="Account status">
              <p className="account-popover-status">Not signed in</p>
            </div>
          )}
        </div>
      </nav>
      <div className="app-content">
        <Outlet />
      </div>
      <LegalFooter />
    </>
  );
};

export default Layout;
